import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import RitualGuard from './components/RitualGuard';
// import DebugRitual from './components/DebugRitual'; // Moved to admin panel

// Pages
import Landing from './pages/Landing';
import Register from './pages/Register';
import BestPractices from './pages/BestPractices';
import OnboardingProfile from './pages/OnboardingProfile';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import MHBStore from './pages/MHBStore';
import Dashboard from './pages/Dashboard';
import DynamicDashboard from './pages/DynamicDashboard';
import RealityGarden from './pages/RealityGarden';
import MyLife from './pages/MyLife';
import CreatePost from './pages/CreatePost';
import Groves from './pages/Groves';
import Events from './pages/Events';
import DailyRitual from './pages/DailyRitual';
import Profile from './pages/Profile';
import MeditationTimer from './pages/MeditationTimer';
import PoliticalFeed from './pages/PoliticalFeed';
import ConspiratorialFeed from './pages/ConspiratorialFeed';
import TrollHoleFeed from './pages/TrollHoleFeed';
import AdminDashboard from './pages/AdminDashboard';
import PrintifyUrlEditor from './pages/PrintifyUrlEditor';
import LowerSidebarManager from './pages/LowerSidebarManager';
import AdminUserManagement from './pages/AdminUserManagement';
import ConsciousCommerceAdmin from './pages/ConsciousCommerceAdmin';
import BoostSuccess from './pages/BoostSuccess';
import ProtocolComplete from './pages/ProtocolComplete';

// Protected Route component with Ritual Guard DISABLED FOR TESTING
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // Auto-trigger re-enabled via RitualGuard (has exempt paths + error fallback to prevent loops)
  return <RitualGuard>{children}</RitualGuard>;
};

// Public Route component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return !isAuthenticated ? children : <Navigate to="/dashboard" />;
};

function AppRoutes() {
  return (
    <Layout>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={
          <PublicRoute>
            <Landing />
          </PublicRoute>
        } />
        <Route path="/register" element={
          <PublicRoute>
            <BestPractices />
          </PublicRoute>
        } />
        <Route path="/old-register" element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } />
        <Route path="/login" element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } />
        <Route path="/forgot-password" element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        } />
        
        {/* New onboarding flow */}
        <Route path="/onboarding-profile" element={<OnboardingProfile />} />
        
        {/* Public MHB Store */}
        <Route path="/store" element={<MHBStore />} />

        {/* Guest Feed Route - No authentication required */}
        <Route path="/guest-feed" element={<Dashboard isGuestMode={true} />} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />

        <Route path="/dynamic-feed" element={
          <ProtectedRoute>
            <DynamicDashboard />
          </ProtectedRoute>
        } />

        <Route path="/daily-ritual" element={
          <ProtectedRoute>
            <DailyRitual />
          </ProtectedRoute>
        } />

        {/* Protocol Complete - Bypass RitualGuard since it's part of ritual completion */}
        <Route path="/protocol-complete" element={
          <div>
            {/* Simple auth check without ritual guard */}
            <ProtocolComplete />
          </div>
        } />

        <Route path="/reality-garden" element={
          <ProtectedRoute>
            <RealityGarden />
          </ProtectedRoute>
        } />

        <Route path="/my-life" element={
          <ProtectedRoute>
            <MyLife />
          </ProtectedRoute>
        } />

        <Route path="/create-post" element={
          <ProtectedRoute>
            <CreatePost />
          </ProtectedRoute>
        } />

        <Route path="/boost-success" element={
          <ProtectedRoute>
            <BoostSuccess />
          </ProtectedRoute>
        } />

        {/* Placeholder routes for future implementation */}
        <Route path="/groves" element={
          <ProtectedRoute>
            <Groves />
          </ProtectedRoute>
        } />

        <Route path="/events" element={
          <ProtectedRoute>
            <Events />
          </ProtectedRoute>
        } />

        <Route path="/profile" element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } />

        <Route path="/meditation-timer" element={<MeditationTimer />} />

        {/* Three Circles of Interhell - Moderation Feeds */}
        <Route path="/political" element={
          <ProtectedRoute>
            <PoliticalFeed />
          </ProtectedRoute>
        } />

        <Route path="/conspiratorial" element={
          <ProtectedRoute>
            <ConspiratorialFeed />
          </ProtectedRoute>
        } />

        <Route path="/troll-hole" element={
          <ProtectedRoute>
            <TrollHoleFeed />
          </ProtectedRoute>
        } />

        {/* Admin Dashboard */}
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } />

        {/* Admin User Management */}
        <Route path="/admin/users" element={
          <ProtectedRoute>
            <AdminUserManagement />
          </ProtectedRoute>
        } />

        {/* Conscious Commerce Admin */}
        <Route path="/admin/conscious-commerce" element={
          <ProtectedRoute>
            <ConsciousCommerceAdmin />
          </ProtectedRoute>
        } />

        {/* Printify URL Editor Admin */}
        <Route path="/admin/printify-urls" element={
          <ProtectedRoute>
            <PrintifyUrlEditor />
          </ProtectedRoute>
        } />

        <Route path="/admin/lower-sidebar" element={
          <ProtectedRoute>
            <LowerSidebarManager />
          </ProtectedRoute>
        } />

        <Route path="/daily-affirmation" element={
          <ProtectedRoute>
            <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl mb-4">🙏</div>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">Daily Affirmation</h1>
                <p className="text-gray-600">Coming soon - Your personal affirmation center</p>
              </div>
            </div>
          </ProtectedRoute>
        } />

        {/* Public Member "My Life" Profile - root URL domain.com/username. Reuses the MyLife component in read-only public mode. MUST stay at the bottom so it doesn't intercept other routes */}
        <Route path="/:username" element={<MyLife />} />

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;